import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Clock, MoreHorizontal } from "lucide-react";
import { RelativeLabel } from "@nurion/ui/relative-label";

import { Dialog } from "~/components/Dialog";
import { EmptyState } from "~/components/EmptyState";
import { useScheduledMessages } from "~/hooks/useScheduledMessages";
import type { RoomSummary } from "~/lib/matrix";
import { absoluteFallback, LOCAL_ZONE } from "~/lib/relative-time";
import {
  cancel,
  editBodyAndReschedule,
  retryNow,
  type ScheduledMessageRow,
} from "~/lib/scheduled-messages";
import { ICON_STROKE } from "~/lib/icon";

export function ScheduledQueue({ rooms }: { rooms: RoomSummary[] }) {
  const { items, isAvailable } = useScheduledMessages();
  return <ScheduledQueueContent rooms={rooms} items={items} isAvailable={isAvailable} />;
}

export function ScheduledQueueContent({
  rooms,
  items,
  isAvailable,
}: {
  rooms: RoomSummary[];
  items: ScheduledMessageRow[];
  isAvailable: boolean;
}) {
  const [editing, setEditing] = useState<ScheduledMessageRow | null>(null);
  const [now, setNow] = useState(Date.now());
  const roomById = new Map(rooms.map((room) => [room.id, room]));

  useEffect(() => {
    if (items.length === 0) return;
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, [items.length]);

  const emptyIcon = (
    <Clock
      size={48}
      strokeWidth={ICON_STROKE}
      style={{ color: "var(--chat-fg-muted)", opacity: 0.6 }}
    />
  );

  if (!isAvailable) {
    return (
      <ViewShell count={0}>
        <EmptyState
          icon={emptyIcon}
          primary="Scheduled unavailable"
          secondary="Sign in to see scheduled messages."
        />
      </ViewShell>
    );
  }

  return (
    <ViewShell count={items.length}>
      {items.length === 0 ? (
        <EmptyState
          icon={emptyIcon}
          primary="No scheduled messages"
          secondary="Messages you schedule appear here."
        />
      ) : (
        <ul style={{ flex: 1, overflowY: "auto", listStyle: "none", margin: 0, padding: 0 }}>
          {items.map((item) => (
            <li key={item.id}>
              <ScheduledRow
                item={item}
                room={roomById.get(item.roomId) ?? null}
                now={now}
                onEdit={() => setEditing(item)}
              />
            </li>
          ))}
        </ul>
      )}
      {editing && <EditDialog item={editing} onClose={() => setEditing(null)} />}
    </ViewShell>
  );
}

function ViewShell({ count, children }: { count: number; children: ReactNode }) {
  return (
    <section
      data-testid="scheduled-queue"
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        height: "100%",
        background: "var(--chat-surface-app)",
        color: "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--chat-space-3)",
          height: "var(--chat-timeline-room-header-height)",
          padding: "0 var(--chat-space-4)",
          borderBottom: "1px solid var(--chat-surface-border)",
          flexShrink: 0,
        }}
      >
        <Clock size={16} strokeWidth={ICON_STROKE} style={{ color: "var(--chat-fg-secondary)" }} />
        <h2
          style={{
            flex: 1,
            margin: 0,
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-display)",
            fontSize: "var(--chat-type-display-sm-size)",
            lineHeight: "var(--chat-type-display-sm-line)",
            fontWeight: 300,
          }}
        >
          Scheduled
          <span style={countStyle}>· {count}</span>
        </h2>
      </header>
      {children}
    </section>
  );
}

function ScheduledRow({
  item,
  room,
  now,
  onEdit,
}: {
  item: ScheduledMessageRow;
  room: RoomSummary | null;
  now: number;
  onEdit: () => void;
}) {
  const dueSoon = item.status !== "failed" && item.sendAt - now < 5 * 60_000;
  const failed = item.status === "failed";
  const borderColor = failed
    ? "var(--chat-state-danger)"
    : dueSoon
      ? "var(--chat-fg-secondary)"
      : "transparent";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        height: 72,
        padding: "0 var(--chat-space-4)",
        borderLeft: `2px solid ${borderColor}`,
        borderBottom: "1px solid var(--chat-surface-border)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={previewStyle}>{preview(item.body)}</span>
        <span style={metaStyle}>
          {room?.name ?? "Unknown room"} · {absoluteFallback(item.sendAt)} ·{" "}
          <RelativeLabel
            key={now}
            value={new Date(item.sendAt).toISOString()}
            zone={LOCAL_ZONE}
            fallback={absoluteFallback(item.sendAt)}
          />
        </span>
      </div>
      {failed && (
        <button
          type="button"
          onClick={() => void retryNow(item.id)}
          className="chat-button"
          style={textButtonStyle}
        >
          Retry
        </button>
      )}
      <button
        type="button"
        aria-label="Edit scheduled message"
        onClick={onEdit}
        className="chat-button"
        style={iconButtonStyle}
      >
        <MoreHorizontal size={16} strokeWidth={ICON_STROKE} />
      </button>
      <button
        type="button"
        onClick={() => void cancel(item.id)}
        className="chat-button"
        style={textButtonStyle}
      >
        Cancel
      </button>
    </div>
  );
}

function EditDialog({ item, onClose }: { item: ScheduledMessageRow; onClose: () => void }) {
  const [body, setBody] = useState(item.body);
  const [sendAt, setSendAt] = useState(() => toDateTimeLocal(item.sendAt));
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    const next = new Date(sendAt).getTime();
    if (body.trim().length === 0) {
      setError("Message cannot be empty.");
      return;
    }
    if (!Number.isFinite(next) || next <= Date.now()) {
      setError("Choose a future time.");
      return;
    }
    try {
      await editBodyAndReschedule(item.id, body, next);
      onClose();
    } catch {
      setError("Could not save changes.");
    }
  };
  const titleId = "scheduled-edit-dialog-title";
  const bodyId = "scheduled-edit-message";
  const sendAtId = "scheduled-edit-send-at";
  return (
    <Dialog labelledBy={titleId} onClose={onClose} width="420px" panelStyle={editDialogPanelStyle}>
      <h3 id={titleId} style={dialogTitleStyle}>
        Edit scheduled message
      </h3>
      <div style={fieldGroupStyle}>
        <label htmlFor={bodyId} style={labelStyle}>
          Message
        </label>
        <textarea
          id={bodyId}
          value={body}
          onChange={(e) => setBody(e.currentTarget.value)}
          style={inputStyle}
        />
      </div>
      <div style={fieldGroupStyle}>
        <label htmlFor={sendAtId} style={labelStyle}>
          Send at
        </label>
        <input
          id={sendAtId}
          type="datetime-local"
          value={sendAt}
          onChange={(e) => setSendAt(e.currentTarget.value)}
          style={inputStyle}
        />
      </div>
      {error && (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--chat-space-2)" }}>
        <button type="button" onClick={onClose} className="chat-button" style={textButtonStyle}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          className="chat-button"
          style={textButtonStyle}
        >
          Save
        </button>
      </div>
    </Dialog>
  );
}

const countStyle: CSSProperties = {
  marginLeft: "var(--chat-space-2)",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-md-size)",
  lineHeight: "var(--chat-type-body-md-line)",
  fontWeight: 400,
};
const previewStyle: CSSProperties = {
  color: "var(--chat-fg-primary)",
  fontSize: "var(--chat-type-body-md-size)",
  lineHeight: "var(--chat-type-body-md-line)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const metaStyle: CSSProperties = {
  color: "var(--chat-fg-secondary)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const textButtonStyle: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--chat-fg-secondary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
  cursor: "pointer",
  padding: 0,
};
const iconButtonStyle: CSSProperties = {
  width: 24,
  height: 24,
  border: "none",
  borderRadius: "var(--chat-radius-sm)",
  background: "transparent",
  color: "var(--chat-fg-secondary)",
  cursor: "pointer",
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
const editDialogPanelStyle: CSSProperties = {
  gap: "var(--chat-space-3)",
  padding: "var(--chat-space-5)",
};
const dialogTitleStyle: CSSProperties = {
  margin: 0,
  color: "var(--chat-fg-primary)",
  fontFamily: "var(--chat-font-display)",
  fontSize: "var(--chat-type-display-sm-size)",
  lineHeight: "var(--chat-type-display-sm-line)",
  fontWeight: 600,
};
const fieldGroupStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--chat-space-1)",
};
const labelStyle: CSSProperties = {
  color: "var(--chat-fg-secondary)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
};
const inputStyle: CSSProperties = {
  width: "100%",
  border: "none",
  borderBottom: "1px solid var(--chat-surface-raised)",
  background: "transparent",
  color: "var(--chat-fg-primary)",
  fontFamily: "var(--chat-font-sans)",
  fontSize: "var(--chat-type-body-md-size)",
  lineHeight: "var(--chat-type-body-md-line)",
  outline: "none",
  padding: "0 0 var(--chat-space-1)",
  resize: "vertical",
};
const errorStyle: CSSProperties = {
  margin: 0,
  color: "var(--chat-state-danger)",
  fontSize: "var(--chat-type-body-sm-size)",
  lineHeight: "var(--chat-type-body-sm-line)",
};

function preview(body: string): string {
  return body.length > 80 ? `${body.slice(0, 80)}...` : body;
}

function toDateTimeLocal(ts: number): string {
  const date = new Date(ts);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
